/**
 * Lightweight JWT payload decoder. We only ever read claims out of the
 * Journix product-admin access token (`productAdminOf`, subject, expiry).
 * Signature validation is the backend's job — this is a read-only
 * client-side parse.
 *
 * Returns `null` on any malformed input rather than throwing, so callers can
 * branch cleanly without try/catch noise.
 */
export interface JwtPayload {
  sub?: string;
  aud?: string | string[];
  exp?: number;
  iat?: number;
  productAdminOf?: string;
  email?: string;
  name?: string;
  [key: string]: unknown;
}

export function decodeJwtPayload(token: string | null | undefined): JwtPayload | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const payloadB64 = parts[1];
  if (!payloadB64) return null;
  try {
    const base64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json =
      typeof atob === 'function' ? atob(padded) : Buffer.from(padded, 'base64').toString('utf-8');
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}
